/**
 * One row per house, the line items across. Every figure is a button that
 * opens the cell panel: what the number is and the workbook cell it was read
 * from. Each header says where its column comes from, as the loan tape's do.
 * Columns added from the Analyst rail follow the workbook's, marked as such.
 */

import React, { useEffect, useRef } from "react";
import type { AddedColumn } from "../../services/portfolioDataService";
import {
	AGGREGATE_LABEL,
	HOUSE_COLUMNS,
	type HouseColumn,
	type HouseRow,
	ORIGIN_TEXT,
	type Origin,
	addedAggregate,
	addedKey,
	addedValue,
	formatAdded,
	formatMonth,
	formatValue,
	totals,
} from "./portfolioModel";

export interface HouseSelection {
	houseId: string;
	lineItem: string;
}

export interface HouseTableProps {
	rows: readonly HouseRow[];
	/** Group the rows under their plat, as All houses does. */
	grouped: boolean;
	/** Inside a plat row: no Lease column, so it fits under the plat's own. */
	compact?: boolean;
	/** Houses a finding points at, marked in the table. */
	highlighted?: ReadonlySet<string>;
	selected: HouseSelection | null;
	onSelect: (selection: HouseSelection) => void;
	/** Columns added from the Analyst rail, after the workbook's. */
	added?: readonly AddedColumn[];
	/** The year on screen, which an added column's value is read for. */
	year?: number;
}

const TH =
	"whitespace-nowrap border-b border-gray-200 bg-[#fafafa] px-3 py-2 align-top font-normal";
const TD = "whitespace-nowrap border-b border-[#f1f2f4] px-0 py-0 text-[#1F2937]";

const columnOrigin = (column: HouseColumn, rows: readonly HouseRow[]): Origin => {
	if (column.key === "vacancy_pct") return "worked";
	const origins = new Set(rows.map((row) => row.cells[column.key]?.origin).filter(Boolean));
	return origins.has("split") ? "split" : "workbook";
};

const HouseTable: React.FC<HouseTableProps> = ({
	rows,
	grouped,
	compact = false,
	highlighted,
	selected,
	onSelect,
	added = [],
	year = 0,
}) => {
	const columns = HOUSE_COLUMNS.filter((column) => column.primary);
	const isSelected = (houseId: string, lineItem: string) =>
		selected?.houseId === houseId && selected.lineItem === lineItem;
	// The cell just opened stays in view when the panel narrows the table.
	const frame = useRef<HTMLDivElement>(null);
	useEffect(() => {
		frame.current
			?.querySelector('[aria-pressed="true"]')
			?.scrollIntoView({ block: "nearest", inline: "nearest" });
	}, [selected]);
	const groups = grouped
		? Array.from(
				rows.reduce((map, row) => {
					const key = String(row.house.plat_name);
					map.set(key, [...(map.get(key) ?? []), row]);
					return map;
				}, new Map<string, HouseRow[]>()),
			)
		: [["", [...rows]] as [string, HouseRow[]]];

	const totalRow = (label: string, subset: readonly HouseRow[]) => {
		const sums = totals(subset);
		return (
			<tr className="bg-[#fafafa] font-semibold" key={`total-${label}`}>
				<td className="sticky left-0 z-[1] whitespace-nowrap border-b border-[#f1f2f4] bg-[#fafafa] px-3 py-2">{label}</td>
				{columns.map((column) => (
					<td
						key={column.key}
						className={`whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2 text-right tabular-nums ${
							(sums[column.key] ?? 0) < 0 ? "text-[#b42318]" : ""
						}`}
					>
						{sums[column.key] === null ? "" : formatValue(sums[column.key], column.kind)}
					</td>
				))}
				{added.map((column) => {
					const value = addedAggregate(column, subset.map((row) => row.house.house_id), year);
					return (
						<td
							key={column.id}
							className="whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2 text-right tabular-nums"
							title={AGGREGATE_LABEL[column.aggregate]}
						>
							{value === null ? "" : formatAdded(value, column)}
							{value !== null && column.aggregate === "mean" && (
								<span className="ml-1 text-[11px] font-normal text-[#6B7280]">avg</span>
							)}
						</td>
					);
				})}
				{!compact && <td className="border-b border-[#f1f2f4] px-3 py-2" />}
			</tr>
		);
	};

	return (
		<div ref={frame} className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
			<table className="w-full border-collapse text-[13px] tabular-nums">
				<thead>
					<tr className="text-left">
						<th scope="col" className={`${TH} sticky left-0 z-[2]`}>
							<span className="text-[12.5px] font-semibold text-[#1F2937]">House</span>
						</th>
						{columns.map((column) => {
							const origin = columnOrigin(column, rows);
							return (
								<th key={column.key} scope="col" className={`${TH} text-right`}>
									<span className="flex flex-col items-end gap-0.5">
										<span className="text-[12.5px] font-semibold text-[#1F2937]">{column.label}</span>
										<span className="text-[11px] text-[#6B7280]">
											{column.unit}
											{origin !== "workbook" && (
												<span className={`font-semibold ${ORIGIN_TEXT[origin]}`}>
													{" · "}
													{origin === "split" ? "split" : "worked out"}
												</span>
											)}
										</span>
									</span>
								</th>
							);
						})}
						{added.map((column) => (
							<th key={column.id} scope="col" data-added-column={column.id} className={`${TH} bg-[#f5f6ff] text-right`} title={column.definition}>
								<span className="flex flex-col items-end gap-0.5">
									<span className="text-[12.5px] font-semibold text-[#1F2937]">{column.label}</span>
									<span className="text-[11px] text-[#6B7280]">
										{column.unit}
										<span className={`font-semibold ${ORIGIN_TEXT.worked}`}> · added in chat</span>
									</span>
								</span>
							</th>
						))}
{!compact && (
						<th scope="col" className={TH}>
							<span className="flex flex-col gap-0.5">
								<span className="text-[12.5px] font-semibold text-[#1F2937]">Lease ends</span>
								<span className="text-[11px] font-normal text-[#6B7280]">New = started this year</span>
							</span>
						</th>
						)}
					</tr>
				</thead>
				<tbody>
					{groups.map(([platName, subset]) => (
						<React.Fragment key={platName || "all"}>
							{grouped && (
								<tr>
									<td
										colSpan={columns.length + added.length + (compact ? 1 : 2)}
										className="border-b border-gray-200 bg-white px-3 pb-1.5 pt-3 text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]"
									>
										{platName} · {subset.length} houses
									</td>
								</tr>
							)}
							{subset.map((row) => (
								<tr key={row.house.house_id} className={highlighted?.has(row.house.house_id) ? "[&>td]:bg-[#fffaeb]" : ""}>
									<td
										className="sticky left-0 z-[1] whitespace-nowrap border-b border-[#f1f2f4] bg-white px-3 py-2 font-semibold text-[#111111]"
										title={`${String(row.house.bed_bath ?? "")} · ${String(row.house.address ?? "")}`}
									>
										<span className="inline-flex items-center gap-1.5">
											{row.shortfall && (
												<span
													className="inline-block h-[7px] w-[7px] rounded-full bg-[#dc6803]"
													title="Collected 10% or more under billed"
												/>
											)}
											{row.house.house_id}
										</span>
									</td>
									{columns.map((column) => {
										const cell = row.cells[column.key];
										const pressed = isSelected(
											row.house.house_id,
											column.key === "vacancy_pct" ? "vacancy_loss" : column.key,
										);
										const value = cell?.value ?? null;
										// Collecting more than was billed (arrears caught up) is
										// good news, so a negative vacancy loss is green, not red.
										const caughtUp = column.key === "vacancy_pct" && value !== null && value < 0;
										const red =
											!caughtUp &&
											((value !== null && value < 0) ||
												(column.key === "dcr" && value !== null && value < 1) ||
												(column.key === "vacancy_pct" && row.shortfall));
										return (
											<td key={column.key} className={TD}>
												<button
													type="button"
													disabled={!cell?.fact}
													onClick={() =>
														onSelect({
															houseId: row.house.house_id,
															lineItem: column.key === "vacancy_pct" ? "vacancy_loss" : column.key,
														})
													}
													aria-pressed={pressed}
													className={`block w-full px-3 py-2 text-right focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#008782] enabled:hover:bg-[#f5f8ff] ${
														pressed ? "bg-[#eef4ff] font-semibold shadow-[inset_0_0_0_2px_#444ce7]" : ""
													} ${cell?.origin === "split" ? "text-[#6B7280]" : ""} ${red ? "text-[#b42318]" : ""} ${caughtUp ? "text-[#067647]" : ""}`}
													title={caughtUp ? "Collected more than billed: a tenant caught up on arrears" : undefined}
												>
													{formatValue(value, column.kind)}
												</button>
											</td>
										);
									})}
									{added.map((column) => {
										const cell = addedValue(column, row.house.house_id, year);
										const key = addedKey(column.id);
										const pressed = isSelected(row.house.house_id, key);
										const value = cell?.value ?? null;
										return (
											<td key={column.id} className={`${TD} bg-[#fafaff]`}>
												<button
													type="button"
													disabled={!cell}
													onClick={() => onSelect({ houseId: row.house.house_id, lineItem: key })}
													aria-pressed={pressed}
													title={cell?.note ?? undefined}
													className={`block w-full px-3 py-2 text-right focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#008782] enabled:hover:bg-[#f5f8ff] ${
														pressed ? "bg-[#eef4ff] font-semibold shadow-[inset_0_0_0_2px_#444ce7]" : ""
													} ${value !== null && value < 0 ? "text-[#b42318]" : ""}`}
												>
													{formatAdded(value, column)}
												</button>
											</td>
										);
									})}
{!compact && (
									<td className="whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2 text-[#5B6472]">
										{formatMonth(row.leaseEnd)}
										{row.newLease && (
											<span className="ml-2 inline-flex h-[20px] items-center rounded-full bg-[#f2f4f7] px-2 text-[11px] font-semibold text-[#3f4247]">
												New
											</span>
										)}
									</td>
									)}
								</tr>
							))}
							{totalRow(grouped ? `${platName} total` : "Total", subset)}
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	);
};

export default HouseTable;
